import { Typography, styled } from "@mui/material";
import type { ComponentPropsWithRef } from "react";

import { StarburstIcon } from "@citrine/atoms/icons";

interface PairProps extends ComponentPropsWithRef<"div"> {
  reversed?: boolean;
}
export const Pair = styled("div", {
  shouldForwardProp: (propName: keyof PairProps) => propName !== "reversed",
})<PairProps>(({ theme, reversed }) => ({
  display: "flex",
  flexDirection: reversed ? "column-reverse" : "column",
  gap: theme.spacing(2),
}));

// User turn: a compact grey bubble, right-aligned (matches the agent flow).
export const Question = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
});

// Assistant turn: full-width content on the panel, no bubble.
export const Answer = styled("div")({
  display: "flex",
});

// TODO: the question bubble below duplicates the agent flow's user MessageBubble
// (agent-chat-message-list.sx). Extract a shared bubble in the pending-question
// follow-up rather than maintaining two copies.
export const Body = styled("div")(({ theme }) => ({
  [`${Question} &`]: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.shape.borderRadiusLg,
    maxWidth: "85%",
    padding: theme.spacing(1, 1.5),
    whiteSpace: "pre-wrap",
  },
  [`${Answer} &`]: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  // Errors stay a tinted full-width block rather than a bubble.
  [`${Answer}.error &`]: {
    backgroundColor: theme.palette.error.background,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
  },
}));

export const UserActionButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
}));

export const AutomaticallyDiscardedMessage = styled(Typography)(
  ({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  }),
);

export const Text = styled(Typography)(({ theme }) => ({
  [`${Answer}.error &`]: {
    color: theme.palette.error.dark,
    fontWeight: 300,
  },
})) as typeof Typography;

// A quiet status line matching the agent's event vocabulary: a teal agent
// glyph + muted caption, rather than an all-caps label on a divider.
export const ModeChangeStatement = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.75),
}));

export const ModeChangeGlyph = styled(StarburstIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
  fontSize: theme.spacing(1.75),
}));

export const ModeChangeStatementText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
})) as typeof Typography;

export const ModelExpertWelcomePrompt = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

interface ModelExpertWelcomePromptLineProps extends ComponentPropsWithRef<"div"> {
  isSamplePrompt?: boolean;
}

export const ModelExpertWelcomePromptLine = styled("div", {
  shouldForwardProp: (propName: keyof ModelExpertWelcomePromptLineProps) =>
    propName !== "isSamplePrompt",
})<ModelExpertWelcomePromptLineProps>(({ theme, isSamplePrompt }) => [
  isSamplePrompt
    ? {
        color: theme.palette.text.secondary,
        fontStyle: "italic",
      }
    : {},
]);

export const References = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const ReferencesList = styled("dl")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBlockStart: 0,
  marginBlockEnd: 0,
}));

export const ReferenceItem = styled("dt")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
